import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SparklesIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LlmExtractionFields } from "@/components/LlmExtractionFields";

type LlmExtraction = components["schemas"]["BillLlmExtractionResponse"];

// Renders nothing for OCR-extracted bills (the endpoint 404s: no LLM record).
export function BillLlmExtractionCard({ billId }: { billId: string }) {
  const [record, setRecord] = useState<LlmExtraction | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchRecord() {
      setLoading(true);
      setFailed(false);
      const { data, error, response } = await api.GET("/admin/bills/{billId}/llm-extraction", {
        params: { path: { billId } },
      });
      if (cancelled) return;
      if (error) {
        // 404 = OCR-extracted bill (no LLM record); anything else is a real failure worth flagging.
        if (response?.status !== 404) setFailed(true);
        setRecord(null);
      } else {
        setRecord(data ?? null);
      }
      setLoading(false);
    }

    fetchRecord();
    return () => {
      cancelled = true;
    };
  }, [billId]);

  // Render nothing until resolved, to avoid a spinner flash on every OCR bill page.
  if (loading || (!record && !failed)) return null;

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SparklesIcon className="size-4 text-muted-foreground" />
          Bill extraction
        </CardTitle>
      </CardHeader>
      <CardContent>
        {failed ? (
          <p className="text-destructive text-sm">Failed to load bill extraction.</p>
        ) : record ? (
          <div className="space-y-4">
            <LlmExtractionFields record={record} />
            <Link
              to="/llm-extractions"
              className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
            >
              View all bill extractions
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
